import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { User, UserWithOrganisation } from '../types/database';

interface AuthContextType {
  user: UserWithOrganisation | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: Partial<User>) => Promise<void>;
  signOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<UserWithOrganisation | null>(null);
  const [loading, setLoading] = useState(true);

  // Very simple organization fetching
  const getDefaultOrganization = () => {
    return {
      id: '1',
      name: 'Kridah Labs',
      subdomain: 'kridah',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      active: true
    };
  };

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      console.log('🔐 Signing in:', email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        throw error;
      }

      if (data.user?.email) {
        console.log('Sign in successful for:', data.user.email);
        
        setUser({
          id: data.user.id,
          email: data.user.email,
          first_name: 'User',
          last_name: 'Account',
          role: 'user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          active: true,
          organisation: getDefaultOrganization()
        });
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, userData: Partial<User>) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password
      });

      if (error) {
        throw error;
      }

      if (data.user?.email) {
        setUser({
          id: data.user.id,
          email: data.user.email,
          first_name: userData.first_name || 'User',
          last_name: userData.last_name || 'Account',
          role: userData.role || 'user',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          active: true,
          organisation: getDefaultOrganization()
        });
      }
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      console.log('🔄 AuthContext: Starting sign out...');
      setLoading(true);
      
      // Clear user state immediately
      setUser(null);
      
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error('❌ Supabase sign out error:', error);
        // Don't throw error, just log it
      } else {
        console.log('✅ Supabase sign out successful');
      }
    } catch (error) {
      console.error('❌ Sign out error:', error);
      // Don't throw error, just log it
    } finally {
      setLoading(false);
      console.log('✅ AuthContext: Sign out process completed');
    }
  };

  const refreshUser = async () => {
    // Simple refresh - just check session
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user?.email) {
      setUser({
        id: session.user.id,
        email: session.user.email,
        first_name: 'User',
        last_name: 'Account',
        role: 'user',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        active: true,
        organisation: getDefaultOrganization()
      });
    }
  };

  useEffect(() => {
    console.log('🚀 AuthProvider initializing...');
    
    // Check session immediately
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user?.email) {
          console.log('Found session for user:', session.user.email);
          setUser({
            id: session.user.id,
            email: session.user.email,
            first_name: 'User',
            last_name: 'Account',
            role: 'user',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            active: true,
            organisation: getDefaultOrganization()
          });
        }
      } catch (error) {
        console.error('Error checking session:', error);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    // Short timeout
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.warn('⚠️ Loading timeout - forcing loading to false');
        setLoading(false);
      }
    }, 2000); // 2 second timeout

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email);
        
        if (event === 'SIGNED_IN' && session?.user?.email) {
          console.log('User signed in:', session.user.email);
          setUser({
            id: session.user.id,
            email: session.user.email,
            first_name: 'User',
            last_name: 'Account',
            role: 'user',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            active: true,
            organisation: getDefaultOrganization()
          });
        } else if (event === 'SIGNED_OUT') {
          console.log('User signed out');
          setUser(null);
        }
        
        setLoading(false);
      }
    );

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, []);

  const value: AuthContextType = {
    user,
    loading,
    signIn,
    signUp,
    signOut,
    refreshUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
